import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Cpu, Brain, UserCheck, Orbit, Library, X, ArrowUpRight } from 'lucide-react';
import { WORLDS } from '../data/studio';
import { Reveal, Eyebrow } from './Reveal';

const ICONS = { BookOpen, Cpu, Brain, UserCheck, Orbit, Library };

const SPANS = [
  'md:col-span-3 md:row-span-2',
  'md:col-span-3',
  'md:col-span-2',
  'md:col-span-2',
  'md:col-span-2',
  'md:col-span-6',
];

export default function WorldsMatrix({ onNavigate }) {
  const [active, setActive] = useState(null);

  return (
    <section id="worlds" className="relative py-28 lg:py-40 nebula-mesh" data-testid="worlds-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>The Parent Organism</Eyebrow>
          <h2 className="font-celestial text-3xl sm:text-4xl lg:text-5xl font-light text-slate-100 max-w-3xl leading-tight">
            Six worlds, one <span className="text-cyan-300 text-glow-cyan">living system</span>
          </h2>
          <p className="mt-5 max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed">
            Four major worlds — Books &amp; Ideas, Apps &amp; Technology, Human, Cosmos — bound together by Kyle's Mind and the Library. Room to grow without ever feeling scattered.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-4">
          {WORLDS.map((w, i) => {
            const Icon = ICONS[w.icon];
            return (
              <Reveal key={w.id} delay={i * 0.08} className={SPANS[i]}>
                <motion.button
                  onClick={() => setActive(w)}
                  whileHover={{ y: -6 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                  className="group relative w-full h-full min-h-[190px] text-left rounded-2xl border border-white/[0.07] bg-gradient-to-b from-slate-900/70 to-slate-950/90 p-7 overflow-hidden transition-colors duration-500"
                  style={{ ['--wa']: w.accent }}
                  data-testid={`world-card-${w.id}`}
                >
                  <div
                    className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-0 group-hover:opacity-25 transition-opacity duration-700"
                    style={{ background: w.accent }}
                  />
                  <div className="relative flex items-start justify-between">
                    <Icon size={22} style={{ color: w.accent }} strokeWidth={1.5} />
                    <ArrowUpRight size={16} className="text-slate-600 group-hover:text-slate-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                  </div>
                  <h3 className="relative font-celestial text-xl sm:text-2xl text-slate-100 mt-8">{w.title}</h3>
                  <p className="relative font-mono-precision text-[10px] uppercase tracking-[0.25em] mt-2" style={{ color: w.accent }}>
                    {w.category}
                  </p>
                  <p className="relative text-sm text-slate-400 mt-3 leading-relaxed max-w-md">{w.tagline}</p>
                </motion.button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-[#02040a]/80 backdrop-blur-md"
            onClick={() => setActive(null)}
            data-testid="world-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.92, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 24 }}
              className="relative max-w-lg w-full rounded-3xl border border-cyan-500/20 bg-slate-950/95 backdrop-blur-2xl p-9 shadow-[0_8px_60px_rgba(0,0,0,0.7)]"
              onClick={(e) => e.stopPropagation()}
              data-testid="world-modal"
            >
              <button
                onClick={() => setActive(null)}
                className="absolute top-5 right-5 text-slate-500 hover:text-slate-200 transition-colors"
                data-testid="world-modal-close-btn"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <div
                className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
                style={{ background: active.accent }}
              />
              <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em]" style={{ color: active.accent }}>
                {active.category}
              </p>
              <h3 className="font-celestial text-3xl text-slate-100 mt-3">{active.title}</h3>
              <p className="text-sm text-slate-300/90 leading-relaxed mt-5">{active.detail}</p>
              <ul className="mt-6 space-y-2.5">
                {active.signals.map((s) => (
                  <li key={s} className="flex items-center gap-3 font-mono-precision text-xs text-slate-400">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: active.accent }} />
                    {s}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { setActive(null); onNavigate(active.id === 'kyles_mind' ? 'mind' : active.id === 'library' || active.id === 'books' ? 'books' : active.id === 'apps' ? 'apps' : 'research'); }}
                className="mt-8 font-mono-precision text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-full border transition-all duration-300 hover:shadow-lg"
                style={{ borderColor: active.accent, color: active.accent }}
                data-testid="world-modal-explore-btn"
              >
                Explore this world
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
